import * as React from "react";
import { cn } from "@/lib/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipContainer = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Only the first tooltip of a run animates. The ones after it, opened
        // while moving along a row of buttons, have `instant-open` and just appear.
        "z-[100000] origin-[var(--radix-tooltip-content-transform-origin)] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md transition-none ease-out data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=closed]:zoom-out-97 data-[state=delayed-open]:zoom-in-97 data-[state=closed]:duration-100 data-[state=delayed-open]:duration-125",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps extends React.PropsWithChildren {
  content: React.ReactNode;
  disabled?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Tooltip({
  children,
  content,
  disabled,
  onOpenChange,
  className,
}: TooltipProps) {
  return (
    <TooltipContainer onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>
        {/* asChild renders nothing for a bare string or number */}
        {React.isValidElement(children) ? children : <span>{children}</span>}
      </TooltipTrigger>
      {!disabled && (
        <TooltipPrimitive.Portal>
          <TooltipContent className={className}>
            {typeof content === "string" && <p>{content}</p>}
            {typeof content !== "string" && content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      )}
    </TooltipContainer>
  );
}

export { TooltipContainer, TooltipContent, TooltipProvider, TooltipTrigger };
